import { useAuthToken } from '@/hooks/useAuthToken';
import { scribeHttpV2 } from '@/utils/scribeHTTP';
import { getTempUserId } from '@/utils/tempUser';
import type { ChatListResponse } from '@heidi/session/contracts';
import { type InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { GET_CHATS } from './useQueryChats';

/** Default page size for infinite chat queries. Exported for use in cache updates. */
export const INFINITE_CHATS_DEFAULT_PAGE_SIZE = 20;

/**
 * Constructs the query key for infinite chat queries.
 * Use this factory to ensure cache updates target the correct key structure.
 */
export const getInfiniteChatsQueryKey = (
  pageSize = INFINITE_CHATS_DEFAULT_PAGE_SIZE,
  query: string | null = null,
  hasSearchQuery = false
) => [GET_CHATS, 'infinite', pageSize, query, hasSearchQuery] as const;

interface UseInfiniteQueryChatsOptions {
  query?: string;
  pageSize?: number;
  enabled?: boolean;
}

export function useInfiniteQueryChats({
  query,
  pageSize = INFINITE_CHATS_DEFAULT_PAGE_SIZE,
  enabled = true,
}: UseInfiniteQueryChatsOptions = {}) {
  const token = useAuthToken();
  const tempUserId = getTempUserId();

  const normalizedQuery = query?.trim() || '';
  const hasSearchQuery = normalizedQuery.length > 0;

  const result = useInfiniteQuery<
    ChatListResponse,
    Error,
    InfiniteData<ChatListResponse, number>,
    [typeof GET_CHATS, 'infinite', number, string | null, boolean],
    number
  >({
    // Include the query in the cache key when searching so each query string
    // has its own backend search result cache.
    queryKey: [
      GET_CHATS,
      'infinite',
      pageSize,
      hasSearchQuery ? normalizedQuery : null,
      hasSearchQuery,
    ],
    enabled: (Boolean(token || tempUserId) && enabled) || false,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const endpoint = hasSearchQuery ? '/chat/chats/search' : '/chat/chats';
      const params: Record<string, string | number | boolean> = {
        page: pageParam,
        page_size: pageSize,
        exclude_workflow_runs: true,
      };

      if (hasSearchQuery) {
        params.q = normalizedQuery;
      }

      const { data } = await scribeHttpV2.get<ChatListResponse>(endpoint, {
        params,
      });
      return data;
    },
    getNextPageParam: (lastPage) => {
      const hasMore = lastPage.page * lastPage.page_size < lastPage.total;
      return hasMore ? lastPage.page + 1 : undefined;
    },
  });

  return {
    data: result.data,
    isLoading: result.isLoading,
    isError: result.isError,
    refetch: result.refetch,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
    // v5 defines `isLoading` as `isPending && isFetching` — a cached (stale)
    // search result's *background* refetch has `isLoading=false` while
    // `isFetching=true`, so a caller that must not treat that refetch as
    // "settled" (e.g. Recents' search loading gate) needs this too.
    isFetching: result.isFetching,
  };
}
